import React, { useState } from 'react';
import {
  Box, Card, CardContent, TextField,
  Button, Typography, InputAdornment,
  Alert, CircularProgress, Stepper,
  Step, StepLabel
} from '@mui/material';
import { PhoneAndroid, Lock, Pin } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { forgotPwSendOtp, forgotPwReset } from '../api/authApi';
import { useApp } from '../context/AppContext';

const steps = ['Enter Mobile', 'Verify OTP', 'New Password'];

export default function ForgotPassword() {
  const { theme, t } = useApp();
  const navigate          = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  // Fields
  const [mobile,      setMobile]      = useState('');
  const [otp,         setOtp]         = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [devOtp,      setDevOtp]      = useState('');

  // UI State
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  // ── Step 1: Send OTP ──────────────────────────────
  const handleSendOtp = async () => {
    setError('');
    if (!mobile || mobile.length !== 10) {
      setError('Please enter a valid 10-digit mobile number.');
      return;
    }
    setLoading(true);
    try {
      const res = await forgotPwSendOtp({ mobileNumber: mobile });
      if (res.data.success) {
        setDevOtp(res.data.message); // DEV: shows OTP
        setActiveStep(1);
      } else {
        setError(res.data.message);
      }
    } catch {
      setError('Cannot connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: Verify OTP (just move to step 3) ──────
  const handleVerifyOtp = () => {
    setError('');
    if (!otp || otp.length < 4) {
      setError('Please enter the OTP sent to your mobile.');
      return;
    }
    setActiveStep(2);
  };

  // ── Step 3: Reset Password ────────────────────────
  const handleResetPassword = async () => {
    setError('');
    if (!newPassword || newPassword.length < 4) {
      setError('Password must be at least 4 characters.');
      return;
    }
    setLoading(true);
    try {
      const res = await forgotPwReset({
        mobileNumber: mobile,
        otpCode:      otp,
        newPassword:  newPassword
      });
      if (res.data.success) {
        setSuccess('Password reset successfully! Redirecting to login...');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setError(res.data.message);
      }
    } catch {
      setError('Cannot connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight:      '100vh',
      background:     'linear-gradient(135deg, #f0f7f4 0%, #e8f5e9 100%)',
      display:        'flex',
      flexDirection:  'column',
      alignItems:     'center',
      justifyContent: 'center',
      p:              2
    }}>

      {/* Logo */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{
          fontWeight: 800,
          color:      theme.primary,
          fontFamily: 'Poppins, sans-serif'
        }}>
          🐄 DairyPro
        </Typography>
        <Typography variant="h6" sx={{ color: '#555', mt: 0.5 }}>
          Forgot Password
        </Typography>
      </Box>

      <Card sx={{
        width:        '100%',
        maxWidth:     480,
        borderRadius: 3,
        boxShadow:    '0 4px 24px rgba(26,122,74,0.10)'
      }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>

          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Alerts */}
          {error   && <Alert severity="error"   sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>}

          {/* DEV OTP hint */}
          {devOtp && activeStep >= 1 && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2, fontSize: '0.8rem' }}>
              {devOtp}
            </Alert>
          )}

          {/* ── Step 1: Mobile ── */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="body2" sx={{ color: '#888', mb: 2 }}>
                Enter your mobile number registered with your dairy center.
              </Typography>
              <TextField
                fullWidth
                label="Mobile Number"
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneAndroid sx={{ color: theme.primary }} />
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 3 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography
                  variant="body2"
                  onClick={() => navigate('/login')}
                  sx={{ color: theme.primary, fontWeight: 700, cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' } }}
                >
                  ← Back to Login
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleSendOtp}
                  disabled={loading}
                  sx={{
                    background:   `linear-gradient(135deg,${theme.primary},${theme.hover})`,
                    borderRadius: 2,
                    px:           3,
                    fontWeight:   700
                  }}
                >
                  {loading ? <CircularProgress size={22} color="inherit" /> : 'Request OTP →'}
                </Button>
              </Box>
            </Box>
          )}

          {/* ── Step 2: OTP ── */}
          {activeStep === 1 && (
            <Box>
              <Typography variant="body2" sx={{ color: '#888', mb: 2 }}>
                Enter the OTP sent to <strong>{mobile}</strong>
              </Typography>
              <TextField
                fullWidth
                label="OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Pin sx={{ color: theme.primary }} />
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 3 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  variant="outlined"
                  onClick={() => setActiveStep(0)}
                  sx={{ borderRadius: 2, color: theme.primary, borderColor: theme.primary }}
                >
                  ← Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleVerifyOtp}
                  sx={{
                    background:   `linear-gradient(135deg,${theme.primary},${theme.hover})`,
                    borderRadius: 2,
                    px:           3,
                    fontWeight:   700
                  }}
                >
                  Next →
                </Button>
              </Box>
            </Box>
          )}

          {/* ── Step 3: New Password ── */}
          {activeStep === 2 && (
            <Box>
              <Typography variant="body2" sx={{ color: '#888', mb: 2 }}>
                Set your new password.
              </Typography>
              <TextField
                fullWidth
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                inputProps={{ minLength: 4 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: theme.primary }} />
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 3 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  variant="outlined"
                  onClick={() => setActiveStep(1)}
                  sx={{ borderRadius: 2, color: theme.primary, borderColor: theme.primary }}
                >
                  ← Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleResetPassword}
                  disabled={loading}
                  sx={{
                    background:   `linear-gradient(135deg,${theme.primary},${theme.hover})`,
                    borderRadius: 2,
                    px:           3,
                    fontWeight:   700
                  }}
                >
                  {loading
                    ? <CircularProgress size={22} color="inherit" />
                    : '✓ Reset Password'}
                </Button>
              </Box>
            </Box>
          )}

        </CardContent>
      </Card>
    </Box>
  );
}